import { useMutation, UseMutationOptions, QueryClient } from "@tanstack/react-query"
import { useAnalytics } from "@/hooks"
import { toast } from "sonner"
import { isForbiddenInDemoError } from "@/components/task/forbidden.error"
import { openDemoDialog } from "@/components/demo.dialog"

export interface UseCosineMutationOptions<TData = unknown, TVariables = void, TContext = unknown>
  extends UseMutationOptions<TData, Error, TVariables, TContext> {
  showToast?: boolean
  event?: string
}

export const defaultOnError = (error: Error) => toast.error("Error", { description: error.message })

export const useCosineMutation = <TData = unknown, TVariables = void, TContext = unknown>(
  options: UseCosineMutationOptions<TData, TVariables, TContext>,
  queryClient?: QueryClient,
) => {
  const { track } = useAnalytics()
  const defaults: UseCosineMutationOptions<TData, TVariables, TContext> = {
    showToast: true,
  }
  options = { ...defaults, ...options }
  const newOptions: UseCosineMutationOptions<TData, TVariables, TContext> = {
    ...options,
    onSettled: (data, error, variables, context) => {
      options?.onSettled?.(data, error, variables, context)
      if (options.event) {
        track(options.event)
      }
    },
    onError: (error, variables, context) => {
      const { onError, showToast } = options
      const isForbiddenInDemo = isForbiddenInDemoError(error)
      if (isForbiddenInDemo) {
        openDemoDialog()
      } else if (showToast) {
        // eslint-disable-next-line no-console
        console.trace(error.message)
        defaultOnError(error)
      }
      onError?.(error, variables, context)
    },
  }
  return useMutation<TData, Error, TVariables, TContext>(newOptions, queryClient)
}
